import React from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDebounce } from 'use-debounce'

import {
  Avatar,
  Button,
  Checkbox,
  Input,
  Loading,
  ModalBox,
  useModalContext,
} from '@fourthwall/components'
import { SearchIcon } from '@fourthwall/icons'
import { pluralize } from '@fourthwall/utils/dist/string'

import { useMembersInfiniteQuery } from './hooks/useMembersInfiiniteQuery'
import * as S from './styled'

type SelectedMember = {
  id: number
  nickname: string
  avatarUrl?: string
}

type Props = {
  selectedMembers?: SelectedMember[]
  onSave: (selectedMembers: SelectedMember[]) => void
}

export const SelectMembers = ({ selectedMembers, onSave }: Props) => {
  const modal = useModalContext()
  const [innerSelectedMembers, setSelectedMembers] = React.useState<SelectedMember[]>(
    selectedMembers || [],
  )
  const [searchQuery, setSearchQuery] = React.useState<string>()
  const [searchQueryDebounced] = useDebounce(searchQuery, 300)

  const { membersInfiniteQuery } = useMembersInfiniteQuery(searchQueryDebounced)

  const members = membersInfiniteQuery.data?.pages.flatMap((page) => page.members)

  const renderContent = () => {
    if (membersInfiniteQuery.isFetching) {
      return (
        <S.EmptyState>
          <Loading isLoading />
        </S.EmptyState>
      )
    }

    if (!searchQueryDebounced || searchQueryDebounced.length < 3) {
      return <S.EmptyState>Search for members</S.EmptyState>
    }

    if (!members?.length) {
      return <S.EmptyState>No members found</S.EmptyState>
    }

    return (
      <S.TableContainer id="scrollableDiv">
        {members?.map((member) => (
          <S.Container>
            <S.CheckboxContainer>
              <Checkbox
                value={
                  !!innerSelectedMembers.find((selectedMember) => selectedMember.id === member.id)
                }
                onValueChange={(value) => {
                  if (value) {
                    setSelectedMembers((prevSelectedMembers) => [
                      ...prevSelectedMembers,
                      {
                        id: member.id,
                        nickname: member.nickname,
                        avatarUrl: member.avatarUrl,
                      },
                    ])
                  } else {
                    setSelectedMembers((prevSelectedMembers) =>
                      prevSelectedMembers.filter(
                        (prevSelecterMember) => prevSelecterMember.id !== member.id,
                      ),
                    )
                  }
                }}
              />
            </S.CheckboxContainer>
            <Avatar name={member.nickname} imageUrl={member.avatarUrl} size="small" />
            <S.Nickname>{member.nickname}</S.Nickname>
          </S.Container>
        ))}
      </S.TableContainer>
    )
  }

  return (
    <ModalBox
      title="Select members"
      onClose={modal.close}
      footer={
        <>
          {innerSelectedMembers.length} {pluralize(innerSelectedMembers.length, 'member')} selected
        </>
      }
      buttons={[
        <Button label="Cancel" appearance="secondary" onClick={modal.close} />,
        <Button
          label="Save"
          appearance="primary"
          onClick={() => {
            onSave(innerSelectedMembers)
            modal.close()
          }}
        />,
      ]}
    >
      <Input
        size="medium"
        placeholder="Search members"
        fullWidth
        leftIcon={SearchIcon}
        onValueChange={setSearchQuery}
        value={searchQuery}
      />
      <InfiniteScroll
        dataLength={members?.length || 0}
        next={membersInfiniteQuery.fetchNextPage}
        hasMore={!!membersInfiniteQuery.hasNextPage}
        loader={null}
        scrollableTarget="scrollableDiv"
        style={{
          overflow: 'none',
        }}
      >
        <Loading isLoading={membersInfiniteQuery.isFetching}>{renderContent()}</Loading>
      </InfiniteScroll>
    </ModalBox>
  )
}
